/**
 * Zustand store for AssemblyViewer state management
 */

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { ViewerActions, ViewerState } from "./store/types";
import { createUserAuthSlice } from "./store/authSlice";
import { createConnectionSlice } from "./store/connectionSlice";
import { createGuiSlice } from "./store/guiSlice";
import { createSceneSlice } from "./store/sceneSlice";
import { createSelectionSlice } from "./store/selectionSlice";
import { createModulesSlice } from "./store/modulesSlice";
import { createSelectedModuleSlice } from "./store/selectedModuleSlice";
import { createConnectionPreviewSlice } from "./store/connectionPreviewSlice";
import { createInstancesSlice } from "./store/instancesSlice";
import { createIKSlice } from "./store/ikSlice";
import { createJointsSlice } from "./store/jointsSlice";
import { createEditLinksSlice } from "./store/editLinksSlice";
import { createEndCoordsSlice } from "./store/endCoordsSlice";
import { createHighlightingSlice } from "./store/highlightingSlice";
import { createSelfCollisionSlice } from "./store/selfCollisionSlice";
import { createEndCoordsGizmoSlice } from "./store/endCoordsGizmoSlice";
import { createJointGroupsSlice } from "./store/jointGroupsSlice";
import { createJointSettingsSlice } from "./store/jointSettingsSlice";
import { createMimicSettingsSlice } from "./store/mimicSettingsSlice";
import { createJointAxisVisualizationSlice } from "./store/jointAxisVisualizationSlice";
import { createChainJointsSlice } from "./store/chainJointsSlice";
import { createDragSlice } from "./store/dragSlice";
import { createResetSlice } from "./store/resetSlice";

export const useViewerStore = create<ViewerState & ViewerActions>()(
  immer((...args) => ({
    ...createUserAuthSlice(...args),
    ...createConnectionSlice(...args),
    ...createGuiSlice(...args),
    ...createSceneSlice(...args),
    ...createSelectionSlice(...args),
    ...createModulesSlice(...args),
    ...createSelectedModuleSlice(...args),
    ...createConnectionPreviewSlice(...args),
    ...createInstancesSlice(...args),
    ...createIKSlice(...args),
    ...createJointsSlice(...args),
    ...createEditLinksSlice(...args),
    ...createEndCoordsSlice(...args),
    ...createHighlightingSlice(...args),
    ...createSelfCollisionSlice(...args),
    ...createEndCoordsGizmoSlice(...args),
    ...createJointGroupsSlice(...args),
    ...createJointSettingsSlice(...args),
    ...createMimicSettingsSlice(...args),
    ...createJointAxisVisualizationSlice(...args),
    ...createChainJointsSlice(...args),
    ...createDragSlice(...args),
    ...createResetSlice(...args),
  }))
);

// Selector hooks for performance
export const useUser = () => useViewerStore((state) => state.user);
export const useUserLoading = () => useViewerStore((state) => state.userLoading);
export const useUserError = () => useViewerStore((state) => state.userError);
export const useGuiElements = () => useViewerStore((state) => state.guiElements);
export const useGuiContainerChildren = () => useViewerStore((state) => state.guiContainerChildren);
export const useMeshes = () => useViewerStore((state) => state.meshes);
export const useCamera = () => useViewerStore((state) => state.camera);
export const useBackgroundColor = () => useViewerStore((state) => state.backgroundColor);
export const useConnected = () => useViewerStore((state) => state.connected);
export const useSelectedMeshUuid = () => useViewerStore((state) => state.selectedMeshUuid);
export const useModules = () => useViewerStore((state) => state.modules);
export const useAssetsBaseUrl = () => useViewerStore((state) => state.assetsBaseUrl);
export const useModuleSearchTerm = () => useViewerStore((state) => state.moduleSearchTerm);
export const useModuleSelectedCategory = () => useViewerStore((state) => state.moduleSelectedCategory);
export const useSelectedModuleInstanceId = () => useViewerStore((state) => state.selectedModuleInstanceId);
export const useSelectedModuleName = () => useViewerStore((state) => state.selectedModuleName);
export const useSelectedModulePosition = () => useViewerStore((state) => state.selectedModulePosition);
export const useSelectedModuleLeafLinks = () => useViewerStore((state) => state.selectedModuleLeafLinks);
export const useSelectedModuleRootLink = () => useViewerStore((state) => state.selectedModuleRootLink);
export const useSelectedModuleCanBeChild = () => useViewerStore((state) => state.selectedModuleCanBeChild);
export const useSelectedModuleHasServo = () => useViewerStore((state) => state.selectedModuleHasServo);
export const useAvailableConnections = () => useViewerStore((state) => state.availableConnections);
export const useSelectedModulePrefix = () => useViewerStore((state) => state.selectedModulePrefix);
export const useShowConnectionPreview = () => useViewerStore((state) => state.showConnectionPreview);
export const usePreviewChildInstanceId = () => useViewerStore((state) => state.previewChildInstanceId);
export const usePreviewMeshTransforms = () => useViewerStore((state) => state.previewMeshTransforms);
export const useMeshToInstance = () => useViewerStore((state) => state.meshToInstance);
export const useInstances = () => useViewerStore((state) => state.instances);
export const useIKEnabled = () => useViewerStore((state) => state.ikEnabled);
export const useIKGizmos = () => useViewerStore((state) => state.ikGizmos);
export const useIKPositionMask = () => useViewerStore((state) => state.ikPositionMask);
export const useIKRotationMask = () => useViewerStore((state) => state.ikRotationMask);
export const useHiddenGizmoIds = () => useViewerStore((state) => state.hiddenGizmoIds);
export const useJoints = () => useViewerStore((state) => state.joints);
export const useEditLinksMode = () => useViewerStore((state) => state.editLinksMode);
export const useEditLinksInstanceId = () => useViewerStore((state) => state.editLinksInstanceId);
export const useEditLinksModuleId = () => useViewerStore((state) => state.editLinksModuleId);
export const useEditLinksData = () => useViewerStore((state) => state.editLinksData);
export const useEditLinksCanBeChild = () => useViewerStore((state) => state.editLinksCanBeChild);
export const useEditLinksHasServo = () => useViewerStore((state) => state.editLinksHasServo);
export const useEditLinksEndEffectorMode = () => useViewerStore((state) => state.editLinksEndEffectorMode);
export const useEditLinksEndEffectorLink = () => useViewerStore((state) => state.editLinksEndEffectorLink);
export const useEditLinksEndEffectorOffsetXyz = () => useViewerStore((state) => state.editLinksEndEffectorOffsetXyz);
export const useEditLinksEndEffectorOffsetRpy = () => useViewerStore((state) => state.editLinksEndEffectorOffsetRpy);
export const useEditLinksCanEditEndEffector = () => useViewerStore((state) => state.editLinksCanEditEndEffector);
export const useEndCoordsList = () => useViewerStore((state) => state.endCoordsList);
export const useHiddenEndCoords = () => useViewerStore((state) => state.hiddenEndCoords);
export const useAvailableLinks = () => useViewerStore((state) => state.availableLinks);
export const useDraggingInstanceId = () => useViewerStore((state) => state.draggingInstanceId);
export const useDragOffset = () => useViewerStore((state) => state.dragOffset);
export const useHighlightedMeshUuids = () => useViewerStore((state) => state.highlightedMeshUuids);
export const useSelfCollisionEnabled = () => useViewerStore((state) => state.selfCollisionEnabled);
export const useSelfCollisionAvailable = () => useViewerStore((state) => state.selfCollisionAvailable);
export const useCollidingMeshUuids = () => useViewerStore((state) => state.collidingMeshUuids);
export const useCollidingLinkPairs = () => useViewerStore((state) => state.collidingLinkPairs);
export const useEndCoordsGizmoVisible = () => useViewerStore((state) => state.endCoordsGizmoVisible);
export const useEndCoordsGizmoPosition = () => useViewerStore((state) => state.endCoordsGizmoPosition);
export const useEndCoordsGizmoQuaternion = () => useViewerStore((state) => state.endCoordsGizmoQuaternion);
export const useEndCoordsLocalPosition = () => useViewerStore((state) => state.endCoordsLocalPosition);
export const useEndCoordsLocalRotation = () => useViewerStore((state) => state.endCoordsLocalRotation);
export const useEndCoordsSnapEnabled = () => useViewerStore((state) => state.endCoordsSnapEnabled);
export const useEndCoordsPositionSnap = () => useViewerStore((state) => state.endCoordsPositionSnap);
export const useEndCoordsRotationSnap = () => useViewerStore((state) => state.endCoordsRotationSnap);
export const useJointGroups = () => useViewerStore((state) => state.jointGroups);
export const useSuggestedGroups = () => useViewerStore((state) => state.suggestedGroups);
export const useShowSuggestedGroupsModal = () => useViewerStore((state) => state.showSuggestedGroupsModal);
export const useJointSettingsExpanded = () => useViewerStore((state) => state.jointSettingsExpanded);
export const useMimicSettings = () => useViewerStore((state) => state.mimicSettings);
export const useSelectedGroupFilter = () => useViewerStore((state) => state.selectedGroupFilter);
export const useJointAxisVisualization = () => useViewerStore((state) => state.jointAxisVisualization);
export const useChainJointsResult = () => useViewerStore((state) => state.chainJointsResult);
export const useLastTransformUpdateTime = () => useViewerStore((state) => state.lastTransformUpdateTime);
export const useHoverConnectionTarget = () => useViewerStore((state) => state.hoverConnectionTarget);
